import { useEffect, useState } from 'react';
import { useT } from '../i18n/useLanguage';
import type { MessageKey } from '../i18n/messages';
import PlaceholderImage from '../components/PlaceholderImage';

const CARD_COUNT = 4;
const AUTO_ADVANCE_MS = 5000;

// One body-copy key per carousel card. Indexed by the active
// `index` so the rose-pink frame's body paragraph swaps in lockstep
// with the `storyImgSrc(index)` artwork (and re-resolves through
// `t()` whenever the user toggles TH/EN). Length must equal
// `CARD_COUNT`; the array literal is `as const satisfies` against
// `MessageKey[]` so a missing or misspelt key fails at typecheck
// rather than throwing at runtime.
const STORY_BODY_KEYS = [
  'story.body.0',
  'story.body.1',
  'story.body.2',
  'story.body.3',
] as const satisfies readonly MessageKey[];

export default function StorySection() {
  const t = useT();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % CARD_COUNT);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, []);

  const prev = () => setIndex((i) => (i - 1 + CARD_COUNT) % CARD_COUNT);
  const next = () => setIndex((i) => (i + 1) % CARD_COUNT);

  // Indices of the previous and next cards for the side previews
  // that flank the active card. Wrap around at the ends so the
  // carousel reads as a continuous loop (matches the auto-advance
  // wrap-around above).
  const prevIndex = (index - 1 + CARD_COUNT) % CARD_COUNT;
  const nextIndex = (index + 1) % CARD_COUNT;

  // Helper — the carousel artwork is named with zero-padded
  // two-digit indexes (`story-image-01.png` … `story-image-05.png`)
  // so dropping in additional artwork only touches one place.
  // Until each file exists `PlaceholderImage` falls back to its
  // placeholder rendering for that slot.
  const storyImgSrc = (i: number) =>
    `/images/story-image-${String(i + 1).padStart(2, '0')}.png`;

  // NOTE: this section intentionally duplicates the outer "scene"
  // chrome from RegisterSection (top/bottom fork-spoon spacers, the
  // four decorative edge borders, the cream main panel and the
  // rose-pink inner content frame with the cupcake-flanked title
  // header). Per design direction these two sections share the
  // exact same shell while housing different content, and we keep
  // them as two parallel components rather than extracting the
  // shell into a shared layout component so each can drift
  // independently as the campaign evolves.
  return (
    <section
      id="story"
      // Mobile-specific scroll behaviour (mirrors RegisterSection):
      //   • `!min-h-screen` (important) forces the section to
      //     ≥ 100vh on phones. The `!` is required because
      //     `.snap-section { min-height: 80vh }` lives outside
      //     `@layer` in index.css and is declared after
      //     `@tailwind utilities`, so on source-order it would
      //     normally win over a plain `min-h-screen`. Desktop
      //     content (panel + spacers + ring binders) is already
      //     much taller than 100vh, so this is a no-op there.
      //   • The mobile portrait inner frame below carries its own
      //     `snap-start [scroll-margin-top:60px]` so when mandatory
      //     snap settles the frame's top lands just under the
      //     floating header — the decorative top spacer isn't what
      //     hugs the header anymore.
      //   • `md:[scroll-margin-top:-100px]` is kept for desktop
      //     link-click scrolling; on mobile that lives on the
      //     frame instead, so the section has no own margin below
      //     md.
      className="snap-section relative flex !min-h-screen w-full flex-col items-center bg-[#63464500] px-4 md:px-8 lg:px-12 md:[scroll-margin-top:-100px]"
      style={{
        // Decorative diamond-lattice overlay on top of the section's
        // transparent bg colour. The brown #634645 from <main> shows
        // through the SVG's empty interior; the white-at-0.2-opacity
        // stroke draws a subtle diamond grid across the whole section.
        // Painted by the browser BEFORE any child content (it's the
        // element's own background), so the spacers, cream main panel
        // and edge decorations all sit on top of it without needing
        // any z-index gymnastics.
        backgroundImage: "url('/images/diamond-tile.svg')",
        backgroundRepeat: 'repeat',
        backgroundSize: '64px 64px',
      }}
    >
      {/* Top spacer — section background above the panel, tiled with
          the fork-spoon icon as a decorative band. Mobile uses a
          thinner 40px spacer so the portrait frame sits high in
          the section just below the header; from md+ the full
          100px spacer comes back to match the desktop / tablet
          design. */}
      <div
        aria-hidden="true"
        className="h-[40px] w-[90%] md:bg-[url('/images/fork-spoon.svg')] bg-[length:72px_24px] bg-center bg-repeat-x opacity-70 md:h-[100px]"
      />

      {/* Cream main panel ("scene"). The forced `min-h-[calc(100vh)]`
          and centred justification only apply at lg+ so the
          desktop landscape layout still anchors a full-viewport
          scene. Below lg the panel collapses to its content's
          natural height and stacks content from the TOP
          (`justify-start`) — works with the mobile-specific
          `min-h-screen` on the parent section to keep the section
          ≈ 100vh tall while the mobile portrait frame sits high
          (just below the header via the frame's own
          `scroll-margin-top:60px`). Padding shrinks from p-10 →
          p-2 below md so the frame keeps more width on phones. */}
      <div
        className="relative min-h-[calc(min(100vh_-_256px))] isolate z-10 flex w-full max-w-[1874px] flex-col justify-start items-center overflow-hidden p-2 md:p-10 lg:min-h-[calc(100vh)] lg:justify-center"
        style={{
          backgroundSize: '160px',
        }}
      >
        {/* Desktop / tablet-landscape inner content frame — Figma
            150:4216. Rose-pink rounded panel with the diamond
            pattern. Background is the Figma frame vector PNG
            stretched to fit so the rose border + pattern scale
            together. Padding is in % so it tracks the frame's
            inner cutout regardless of size. Hidden below lg in
            favour of the portrait mobile frame rendered after
            this block. */}
        <div
          className="relative mx-3 my-3 hidden aspect-[3033/1780] max-h-[85vh] w-full flex-col items-center gap-3 overflow-hidden px-[5%] py-[4%] md:mx-6 md:my-4 lg:flex lg:gap-4"
          style={{
            backgroundImage: "url('/images/register-frame-vector.png')",
            // `contain` + the matching container aspect-ratio above keep
            // the rose-pink frame PNG (native 3033×1780, ≈1.704:1) at
            // its true aspect. With the container itself locked to the
            // same ratio, `contain` actually paints edge-to-edge in
            // the common case; it only kicks in as a safety on tall
            // viewports where `max-h-[85vh]` clamps the aspect-ratio
            // box and browsers may break the ratio. Backed up by
            // `backgroundPosition: 'center'` so any tiny inset band
            // is symmetrical.
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Header block: subtext pill + cupcakes title + divider. */}
          <div className="flex w-full flex-col items-center gap-2 lg:gap-3">
            

            <div className="flex items-center justify-center gap-2 md:gap-3 lg:gap-4">
            <img
                src="/images/cat_head.svg"
                alt=""
                aria-hidden="true"
                className="h-8 w-auto lg:h-12"
                draggable={false}
              />
              <h2 className="story-title-shadow whitespace-nowrap text-center font-display text-2xl font-extrabold text-white md:text-3xl lg:text-[42px] 2xl:text-[58px] 2xl:font-black tracking-[-0.03em]">
                {t('story.title')}
              </h2>
              <img
                src="/images/cat_head.svg"
                alt=""
                aria-hidden="true"
                className="h-8 w-auto lg:h-12"
                draggable={false}
              />
            </div>

            <div className="inline-flex items-center justify-center rounded-pill bg-section-feature 2xl:mt-3 px-6 py-0.5 text-center font-thai text-sm font-bold text-white md:text-lg lg:px-10 lg:py-1 lg:text-2=xl">
              {t('story.subtitle')}
            </div>

            <div className="flex w-1/2 max-w-4xl items-center justify-center gap-2">
              <img
                src="/images/fork-spoon.svg"
                alt=""
                aria-hidden="true"
                className="h-6 w-6"
                draggable={false}
              />
              <div className="h-0.5 flex-1 rounded-full bg-tan" />
              <img
                src="/images/fork-spoon.svg"
                alt=""
                aria-hidden="true"
                className="h-6 w-6"
                draggable={false}
              />
            </div>
          </div>

          {/* Recipe-card carousel + dot indicators — grouped so they
              stay together when the inner content frame distributes
              its children with `justify-between`. Replaces what
              RegisterSection puts here (reward cards row + milestone
              progress bar + total counter).

              Layout: paw button → tiny prev preview → active card
              (full size) → tiny next preview → paw button. The
              side previews give a "peek" of the surrounding cards
              and double as click targets to advance the carousel
              by one in either direction (same behaviour as the
              paw arrow buttons). They're hidden below md so the
              mobile frame doesn't get crowded; from md up they
              render at ~45% and from lg up at ~60% of the active
              card's height, keeping the focus on the centred
              active card. `items-center` vertically centres all
              three card layers so the previews flank the active
              card symmetrically regardless of its height. */}
          <div className="flex w-[85%] max-w-6xl min-h-[50%] flex-1 flex-col items-center gap-4">
            <div className="relative flex w-full min-h-0 flex-1 items-center justify-between gap-2 md:gap-4 lg:gap-6">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="shrink-0 transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
              >
                <img
                  src="/images/cat_paw.png"
                  alt=""
                  aria-hidden="true"
                  className="h-12 w-auto lg:h-14 xl:h-16"
                  draggable={false}
                />
              </button>

              {/* Tiny previous-card preview — clickable shortcut to
                  navigate one step back. Truly small (~25-30% of
                  the row height) and dimmed so it visually
                  subordinates to the active card. Same 16:9
                  aspect as the active card so the previews read
                  as miniature versions of the centre. Hidden
                  below md to free up space in the narrower mobile
                  frame. */}
              <button
                type="button"
                onClick={prev}
                aria-label={`Go to card ${prevIndex + 1}`}
                className="relative hidden aspect-[4/3] h-[45%] w-auto shrink-0 overflow-hidden rounded-lg transition duration-200 ease-out hover:scale-105 hover:opacity-90 md:block"
              >
                <PlaceholderImage
                  src={storyImgSrc(prevIndex)}
                  alt=""
                  hint={`story card ${prevIndex + 1} / ${CARD_COUNT}`}
                  className="h-full w-full object-contain"
                />
              </button>

              <div className="relative aspect-[4/3] h-full max-h-full w-auto max-w-4xl overflow-hidden rounded-lg">
                <PlaceholderImage
                  src={storyImgSrc(index)}
                  alt={`Story card ${index + 1}`}
                  hint={`story card ${index + 1} / ${CARD_COUNT}`}
                  className="h-full w-full object-contain"
                />
              </div>

              {/* Tiny next-card preview — mirror of the prev preview. */}
              <button
                type="button"
                onClick={next}
                aria-label={`Go to card ${nextIndex + 1}`}
                className="relative hidden aspect-[4/3] h-[45%] w-auto shrink-0 overflow-hidden rounded-lg transition duration-200 ease-out hover:scale-105 hover:opacity-90 md:block"
              >
                <PlaceholderImage
                  src={storyImgSrc(nextIndex)}
                  alt=""
                  hint={`story card ${nextIndex + 1} / ${CARD_COUNT}`}
                  className="h-full w-full object-contain"
                />
              </button>

              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="shrink-0 transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
              >
                <img
                  src="/images/cat_paw.png"
                  alt=""
                  aria-hidden="true"
                  className="h-12 w-auto -scale-x-100 lg:h-14 xl:h-16"
                  draggable={false}
                />
              </button>
            </div>

            {/* <div className="flex gap-2" role="tablist" aria-label="Story cards">
              {Array.from({ length: CARD_COUNT }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Go to card ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === index
                      ? 'w-6 bg-section-feature'
                      : 'w-2 bg-section-feature/40'
                  }`}
                />
              ))}
            </div> */}
          </div>

          {/* Body copy — sits where RegisterSection puts its "total
              counter" card. Plain text in the section-feature brown so
              it reads against the rose-pink frame background. */}
          <p className="paw-label min-h-[6.5em] max-w-xl text-center font-thai text-md font-medium leading-relaxed text-section-feature">
            {t(STORY_BODY_KEYS[index])}
          </p>
        </div>

        {/* Mobile / tablet-portrait inner frame — Figma 150:4333.
            Same rose-pink mobile frame asset as the RegisterSection
            mobile (register-frame-vector-mobile.png), reusing the
            tab + portrait artwork. Layout differs: stacked title +
            single-card carousel with paw buttons that carry text
            labels ("ย้อนกลับ" / "ถัดไป") below them, a row of five
            mini story-card thumbnails below the carousel for
            quick-jump navigation, then the body copy. Visible
            below lg only. The whole block is a containment context
            so all text/icon/spacing values are pure cqw of the
            frame's actual rendered width. */}
        <div
          // `snap-start` + `[scroll-margin-top:60px]` make this
          // rose-pink portrait frame the mobile section's snap
          // target. When mandatory snap settles on mobile the
          // frame's TOP edge lands 60px below the viewport top —
          // just below the ≈48px floating header — instead of the
          // section's decorative top spacer being what hugs the
          // header. Harmless on desktop (snap is disabled via
          // `<html>` media query at md+).
          className="lg:hidden relative mx-3 my-auto aspect-[691/881] w-full max-w-[480px] snap-start [scroll-margin-top:60px] overflow-visible [container-type:inline-size]"
          style={{
            backgroundImage:
              "url('/images/register-frame-vector-mobile.png')",
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          {/* Title block — sits in the tab area at the top of the
              frame (Figma 150:4346, top 2.3% / left 5% / 90×11.4%).
              Cat-head icons flank the "เนื้อเรื่อง" title; the
              brown subtitle pill stacks below. */}
          <div className="absolute left-[5%] top-[2.3%] flex h-[11.4%] w-[90%] flex-col items-center justify-center gap-[1cqw]">
            <div className="flex items-center justify-center gap-[1.5cqw]">
              <img
                src="/images/cat_head.svg"
                alt=""
                aria-hidden="true"
                className="w-auto h-[4cqw]"
                draggable={false}
              />
              <h2 className="story-title-shadow whitespace-nowrap text-center font-display font-extrabold text-white text-[4.5cqw]">
                {t('story.title')}
              </h2>
              <img
                src="/images/cat_head.svg"
                alt=""
                aria-hidden="true"
                className="w-auto h-[4cqw]"
                draggable={false}
              />
            </div>

            <div className="inline-flex items-center justify-center rounded-pill bg-section-feature px-[3cqw] py-[0.6cqw] text-center font-thai font-bold text-white text-[2.5cqw]">
              {t('story.subtitle')}
            </div>

            {/* <div className="flex w-full max-w-4xl items-center justify-center gap-2">
              <img
                src="/images/fork-spoon.svg"
                alt=""
                aria-hidden="true"
                className="h-2 w-2 lg:h-9 lg:w-9"
                draggable={false}
              />
              <div className="h-0.5 w-[50%] rounded-full bg-tan" />
              <img
                src="/images/fork-spoon.svg"
                alt=""
                aria-hidden="true"
                className="h-2 w-2 lg:h-9 lg:w-9"
                draggable={false}
              />
            </div> */}
          </div>

          {/* Carousel — paw button + active recipe-card image + paw
              button. Each paw button is a flex column that stacks
              the icon over its Thai label ("ย้อนกลับ" / "ถัดไป").
              The active card is the recipe artwork at 4:3, slightly
              tilted to mirror the Figma's playful 2.5° rotation. */}
          <div className="absolute left-[5%] top-[16%] flex h-[35%] w-[90%] items-center justify-center gap-[2cqw]">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous"
              className="flex shrink-0 flex-col items-center gap-[0.6cqw] transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
            >
              <img
                src="/images/cat_paw.png"
                alt=""
                aria-hidden="true"
                className="w-auto h-[8cqw]"
                draggable={false}
              />
              <span className="paw-label text-[3cqw] mt-1 transition-transform">
                {t('cta.back')}
              </span>
            </button>

            <div className="relative aspect-[4/3] h-full max-h-full w-full overflow-hidden rounded-lg shadow-[ -8px_10px_4px_rgba(99,70,69,0.25)] ">
              <PlaceholderImage
                src={storyImgSrc(index)}
                alt={`Story card ${index + 1}`}
                hint={`story card ${index + 1} / ${CARD_COUNT}`}
                className="h-full w-full object-contain"
              />
            </div>

            <button
              type="button"
              onClick={next}
              aria-label="Next"
              className="flex shrink-0 flex-col items-center gap-[0.6cqw] transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
            >
              <img
                src="/images/cat_paw.png"
                alt=""
                aria-hidden="true"
                className="w-auto -scale-x-100 h-[8cqw]"
                draggable={false}
              />
              <span className="paw-label text-[3cqw] mt-1 transition-transform">
                {t('cta.next')}
              </span>
            </button>
          </div>

          {/* Mini-thumbnail navigation — one tile per story card.
              Mirrors the dot-pagination spirit of Figma 150:4347-50
              but uses the actual story-image artwork so each tile
              previews a different slide. Clicking a tile jumps the
              carousel to that index; the active tile is brightened
              + ringed to distinguish it. Positioned at top 58.4%
              of the frame per Figma metadata. */}
          <ol className="absolute left-[20%] top-[55%] flex w-[60%] items-center justify-between">
            {Array.from({ length: CARD_COUNT }).map((_, i) => {
              const isActive = i === index;
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Go to card ${i + 1}`}
                    aria-current={isActive ? 'true' : undefined}
                    className={`relative aspect-[4/3] w-[10cqw] overflow-hidden rounded-md bg-white transition duration-200 ease-out hover:scale-105 ${
                      isActive
                        ? 'opacity-100 shadow-md scale-105'
                        : 'opacity-60 transition-transform duration-200 ease-out hover:scale-105 hover:opacity-90'
                    }`}
                  >
                    <PlaceholderImage
                      src={storyImgSrc(i)}
                      alt=""
                      hint={`story card ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Body copy — Figma 150:4351 (top 68.74% / left 11.27% /
              77.45%). Plain text in section-feature brown so it
              reads against the rose-pink frame. cqw font size keeps
              line-length and tone consistent with the rest of the
              mobile frame. */}
          <p className="absolute paw-label left-[15%] top-[65%] w-[70%] whitespace-pre-line text-center font-bold font-thai text-section-feature text-[3.5cqw] ">
            {t(STORY_BODY_KEYS[index])}
          </p>
        </div>

      </div>

      {/* Bottom spacer — mirrors the top spacer, tiled with the same
          fork-spoon band so the panel sits between two symmetrical
          decorative bands. Mobile mirrors the trimmed top spacer
          (40px) so the section fits ~100vh tightly; from md+ the
          original 180px band returns for the desktop layout. */}
      <div
        aria-hidden="true"
        className="hidden md:block h-[40px] w-[90%] bg-[url('/images/fork-spoon.svg')] bg-[length:72px_24px] bg-center bg-repeat-x opacity-70 md:h-[180px]"
      />

      {/* Ring-binder ornaments — positioned at the SECTION's bottom
          edge, half above and half below. `bottom-0` anchors each
          image's bottom edge to the section's bottom; `translate-y-1/2`
          then shifts the image down by 50% of its own height so its
          CENTRE lands exactly on the section seam — half painted on
          this section, half overlapping the NEXT section.
          `left-[20%]` / `left-[80%]` + `-translate-x-1/2` centre the
          binders at 20% in from each side (same centring trick used
          throughout this file). z-[20] keeps them above this
          section's own bottom-edge decoration (z-[5]) and above the
          next sibling section in <main> (default z-auto = 0) — the
          section itself doesn't create a stacking context, so the
          z-index escapes to the root and the binders read as a
          continuous "binding" motif bridging the seam.
          The section's `overflow-hidden` was dropped to let the
          bottom halves render outside the section bounds. */}
      <img
        src="/images/ring%20binder.png"
        alt=""
        aria-hidden="true"
        className="hidden lg:block pointer-events-none absolute bottom-0 left-[20%] z-[20] h-24 w-auto -translate-x-1/2 translate-y-1/2 select-none lg:h-32"
        draggable={false}
      />
      <img
        src="/images/ring%20binder.png"
        alt=""
        aria-hidden="true"
        className="hidden lg:block pointer-events-none absolute bottom-0 left-[80%] z-[20] h-24 w-auto -translate-x-1/2 translate-y-1/2 select-none lg:h-32"
        draggable={false}
      />

      {/* Top-edge decorative border — same pattern as the side borders
          but stacked vertically: two thin horizontal accent lines, an
          inner 15px gap, then the 10px gradient flush with the very top
          edge. flex-col-reverse keeps the same JSX child order as the
          side strips so the four bands stay in lockstep if either is
          later tweaked. `from-transparent` lets the corners blend
          continuously into the side gradients. */}
    </section>
  );
}
